import React from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Grid, Card, IconButton, Typography, CardContent, CardHeader } from '@mui/material';

import type { ScenarioContextDto, ScenarioContextLocationDto, VariableDto } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/hooks/useScenarioForm.ts';
import { createFormField } from '@/app/utils/formUtils.ts';
import VariableSuggestionInput from '../Variables/VariableSuggestionInput.tsx';
import { Add, Delete } from '@mui/icons-material';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs, I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces.ts';

type SceneContextLocationsCardProps = {
};

const ScenarioContextLocationsCard: React.FunctionComponent<SceneContextLocationsCardProps> = () => {
  const settingsField = createFormField<ScenarioSettingsType>();
  const contextField = createFormField<ScenarioContextDto>();
  const locationField = createFormField<ScenarioContextLocationDto>();

  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);

  const name = `${settingsField.context}.${contextField.locations}` as const;

  const { control, trigger } = useFormContext<ScenarioSettingsType>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: name,
    keyName: 'key'
  });

  const defaultLocation: ScenarioContextLocationDto = {
    location: translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.newLocation)),
    id: 0
  };

  return (
    (<Grid item component={Card} xs={12}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <Typography variant="h5">
              {translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.locations))}
            </Typography>
            <IconButton onClick={() => append(defaultLocation)} size="large">
              <Add />
            </IconButton>
          </Box>
        }
      />
      <CardContent>
        <Grid container>
          {!fields.length && (
            <Grid container justifyContent="center">
              <Typography>
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.sceneContextLocationsCard.noLocationsAddedYet))}
              </Typography>
            </Grid>
          )}
          {fields.map((field, fieldIndex) => {
            const prefix = `${name}.${fieldIndex}` as const;
            const fullName = `${prefix}.${locationField.location}` as const;

            return (
              (<Grid item xs={12} key={field.key} container wrap="nowrap">
                <Grid item xs>
                  <Controller
                    control={control}
                    name={fullName}
                    defaultValue={field.location}
                    render={({ field: formField }) => (
                      <VariableSuggestionInput
                        inputProps={{
                          margin: 'normal',
                          InputProps: {
                            inputProps: {
                              maxLength: 100,
                              maxRows: 5
                            }
                          }
                        }}
                        value={formField.value}
                        onChange={(value) => formField.onChange(value)}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`${prefix}.${locationField.id}` as const}
                    defaultValue={field.id}
                    render={() => <input type="hidden" />}
                  />
                </Grid>
                <Grid item>
                  <IconButton
                    onClick={() => {
                      remove(fieldIndex);
                      trigger(name);
                    }}
                    size="large"
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>)
            );
          })}
        </Grid>
      </CardContent>
    </Grid>)
  );
};

export default ScenarioContextLocationsCard;
